import { useQuery } from '@tanstack/react-query';
import { bridgeApi } from '@/lib/bridgeApi';

interface SiteSetting {
  id: string;
  setting_key: string;
  setting_value: string;
  category: string;
  is_public: boolean;
}

export const useSiteSettings = () => {
  const { data: settings = [], isLoading, error } = useQuery({
    queryKey: ['site-settings-public'],
    queryFn: async () => {
      // Backend action `site_settings` is optional and may not exist.
      // IMPORTANT: Never throw from here; just fall back to []
      try {
        const response = await bridgeApi.call<any>('site_settings', {});
        if (response?.success && Array.isArray(response.data)) {
          return response.data as SiteSetting[];
        }
        if (Array.isArray(response)) {
          return response as SiteSetting[];
        }
        return [] as SiteSetting[];
      } catch {
        return [] as SiteSetting[];
      }
    },
    staleTime: 2 * 60 * 1000,
    retry: 1,
    refetchOnWindowFocus: true,
    // Avoid throwing into React error boundaries for an optional feature.
    throwOnError: false,
    // If this action is missing (HTTP 400/500), keep UI stable using defaults.
    placeholderData: [] as SiteSetting[],
  });

  const getValue = (key: string, defaultValue: string = '') => {
    if (key === 'logo_url' || key === 'favicon_url' || key === 'hide_frontend_logo') {
      return defaultValue;
    }
    const apiValue = settings.find(s => s.setting_key === key)?.setting_value;
    if (apiValue) return apiValue;
    // Fallback: check localStorage branding cache (survives API failures)
    try {
      const cache = JSON.parse(localStorage.getItem('patakare_branding_cache') || '{}');
      if (cache[key]) return cache[key];
    } catch { /* ignore */ }
    return defaultValue;
  };

  const getNumericValue = (key: string, defaultValue: number) => {
    const value = getValue(key);
    return value ? parseInt(value, 10) : defaultValue;
  };

  return {
    settings,
    isLoading,
    error,
    getValue,
    getNumericValue,
    // Logo settings with defaults
    logoUrl: getValue('logo_url'),
    logoHeight: getNumericValue('logo_height', 56),
    logoHeightMobile: getNumericValue('logo_height_mobile', 48),
    logoHeightFooter: getNumericValue('logo_height_footer', 56),
    logoHeightAuth: getNumericValue('logo_height_auth', 64),
    faviconUrl: getValue('favicon_url'),
    siteName: getValue('site_name', 'Patakare'),
    siteTagline: getValue('site_tagline', 'Your Health Partner'),
    // Contact info
    contactPhone: getValue('contact_phone', '+91 98765 43210'),
    contactEmail: getValue('contact_email', 'support@patakare.com'),
    contactAddress: getValue('contact_address', 'Mumbai, Maharashtra, India'),
    // Social links
    socialFacebook: getValue('social_facebook', 'https://facebook.com/patakare'),
    socialInstagram: getValue('social_instagram', 'https://instagram.com/patakare'),
    socialTwitter: getValue('social_twitter', 'https://twitter.com/patakare'),
    socialYoutube: getValue('social_youtube', 'https://youtube.com/patakare'),
    socialLinkedin: getValue('social_linkedin', 'https://linkedin.com/company/patakare'),
  };
};

export default useSiteSettings;
